import { computed, DestroyRef, effect, inject, Injectable, Provider, signal, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray } from '@angular/forms';
import { isNullish } from '@examdojo/core/util/nullish';
import { ContentItem, ContentItemTypeEnum, ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { SolutionPath, StemMarkScheme } from '@examdojo/models/markscheme-v2';
import { QuestionItemTypeEnum, QuestionItemV2, StemItemV2 } from '@examdojo/models/question';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import { TableContentItemFormGroup } from '@examdojo/question-content-item';
import { provideQuestionCacheImageService, QuestionImageCacheService } from '@examdojo/question-image';
import { combineLatest, defer, from, map, merge, Observable, of, Subject, switchMap, tap, timer } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { getContentJsonPathFromCoordinates as buildContentJsonPath } from '../models/question-items-path.model';
import { isMultipleChoiceQuestion } from '../models/question-items.model';
import {
  ActiveQuestionEditorItem,
  ActiveQuestionEditorItemCoordinates,
  ActiveQuestionEditorSubItemCoordinates,
  createOptionsItemFormGroup,
  createStemFormGroup,
  createStemGroupFormGroup,
  createStimulusFormGroup,
  isStructuralChange,
  populateQuestionItemsFormArray,
  TopLevelFormGroup,
} from './question-item-editor.model';
import { getFormData, QuestionItemsSource } from './question-items-form.model';
import { QuestionItemsHistoryManager } from './question-items-history-manager';
import { QuestionItemsStemMarkschemeHttpService } from './question-items-stem-markscheme-http.service';

export function provideQuestionItemsForm(service: Type<QuestionItemsFormService>): Provider[] {
  return [service, provideQuestionCacheImageService(), { provide: QuestionItemsFormService, useExisting: service }];
}

@Injectable()
export abstract class QuestionItemsFormService {
  constructor() {
    merge(this.updateFormOnSourceEmission(), this.updateHistoryOnFormChanges()).pipe(takeUntilDestroyed()).subscribe();

    effect(() => {
      const activeSubItem = this.activeQuestionSubItem();
      if (!activeSubItem || activeSubItem.value.type !== ContentItemTypeEnum.Image) {
        this.activeQuestionSubItemCoordinates.set(null);
      }
    });

    effect(() => {
      const activeItem = this.activeQuestionItem();
      if (!activeItem) {
        this.activeQuestionItemCoordinates.set(null);
      }
    });
  }

  readonly destroyRef = inject(DestroyRef);

  abstract readonly form: FormArray<TopLevelFormGroup>;

  abstract readonly questionItems$: Observable<QuestionItemV2[] | null>;
  abstract readonly editingOptions$: Observable<QuestionEditingOptions | null>;

  readonly generateSolutionQuestionId$: Observable<number | null> = of(null);

  protected readonly questionImageCacheService = inject(QuestionImageCacheService);
  private readonly questionItemsStemMarkschemeHttpService = inject(QuestionItemsStemMarkschemeHttpService);

  readonly jsonEditorUpdatedQuestionItems$ = new Subject<QuestionItemV2[]>();

  readonly isMultipleChoiceQuestion$ = defer(() => this.questionItems$).pipe(
    map((items) => isMultipleChoiceQuestion(items ?? [])),
  );

  readonly historyManager = new QuestionItemsHistoryManager({ debounceTime: 1000 });

  readonly activeQuestionItem = signal<ActiveQuestionEditorItem | null>(null); // main sidebar. will be used for pdf sync
  readonly activeQuestionItemCoordinates = signal<ActiveQuestionEditorItemCoordinates | null>(null);

  readonly activeQuestionItemType = computed(() => this.activeQuestionItem()?.type);

  readonly activeQuestionSubItem = signal<ImageContentItemFormGroup | TableContentItemFormGroup | null>(null); // additional sidebar. for image and table editing
  readonly activeQuestionSubItemCoordinates = signal<ActiveQuestionEditorSubItemCoordinates | null>(null);

  readonly activeQuestionSubItemContentJsonPath = computed(() => {
    const coordinates = this.activeQuestionSubItemCoordinates();
    const activeItemCoordinates = this.activeQuestionItemCoordinates();
    if (isNullish(coordinates) || isNullish(activeItemCoordinates)) {
      return '';
    }

    return this.getContentJsonPathFromCoordinates({
      ...(coordinates ?? {}),
      ...(activeItemCoordinates ?? {}),
    });
  });

  readonly formStructureChanged$ = new Subject<void>();

  readonly canUndo$ = this.historyManager.canGoBack$;
  readonly canRedo$ = this.historyManager.canGoForward$;
  readonly currentState$ = this.historyManager.currentState$;

  setActiveQuestionItem(
    questionItem: ActiveQuestionEditorItem | null,
    coordinates?: ActiveQuestionEditorItemCoordinates,
  ) {
    if (this.activeQuestionSubItem()) {
      this.activeQuestionSubItem.set(null);
    }

    if (this.activeQuestionItem()?.ctrl === questionItem?.ctrl) {
      return this.activeQuestionItem.set(null);
    }

    this.activeQuestionItem.set(questionItem);
    this.activeQuestionItemCoordinates.set(coordinates ?? null);
  }

  setActiveQuestionSubItem(
    questionSubItem: ImageContentItemFormGroup | TableContentItemFormGroup | null,
    coordinates?: ActiveQuestionEditorSubItemCoordinates,
  ) {
    this.activeQuestionSubItem.set(questionSubItem);
    this.activeQuestionSubItemCoordinates.set(coordinates ?? null);
  }

  addItem<T extends QuestionItemTypeEnum>(type: T, makeActive = true) {
    this.editingOptions$
      .pipe(
        take(1),
        filter(Boolean),
        tap((editingOptions) => {
          switch (type) {
            case QuestionItemTypeEnum.Stem:
              this.form.push(createStemFormGroup(undefined, editingOptions));
              break;
            case QuestionItemTypeEnum.Stimulus:
              this.form.push(createStimulusFormGroup(undefined, editingOptions));
              break;
            case QuestionItemTypeEnum.StemGroup:
              this.form.push(createStemGroupFormGroup(undefined, editingOptions));
              break;
            case QuestionItemTypeEnum.Options:
              this.form.push(createOptionsItemFormGroup(undefined, editingOptions));
              break;
          }

          this.form.markAsDirty();
          this.form.markAsTouched();

          if (makeActive) {
            this.setActiveQuestionItem({ ctrl: this.form.at(-1), type }, { rootItemIndex: this.form.length - 1 }); // TODO:
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  deleteItem(item: ActiveQuestionEditorItem) {
    const itemCtrl = item.ctrl;

    const activeItemCtrl = this.activeQuestionItem()?.ctrl;
    if (activeItemCtrl === itemCtrl) {
      this.activeQuestionItem.set(null);
    }

    const parent = item.ctrl.parent;

    if (parent instanceof FormArray) {
      const parentIndex = parent.controls.findIndex((ctrl) => ctrl === itemCtrl);
      parent.removeAt(parentIndex);
    }

    this.form.markAsDirty();
    this.form.markAsTouched();
  }

  getProcessedFormData(): Observable<QuestionItemV2[]> {
    return from(this.processQuestionItems(getFormData(this.form.getRawValue())));
  }

  undo() {
    this.historyManager.goBack();
  }

  redo() {
    this.historyManager.goForward();
  }

  generateSolutionPathForStem(
    stemCoordinates: [number | null, number],
    generateSolutionQuestionId: number,
  ): Observable<null | SolutionPath[]> {
    const questionItems = this.getFormData();

    const stemId = this.findStemIdByCoordinates(questionItems, {
      groupIndex: stemCoordinates[0],
      stemIndex: stemCoordinates[1],
    });

    if (stemId === null) {
      return of(null);
    }

    return this.questionItemsStemMarkschemeHttpService.generateSolutionPath(
      questionItems,
      generateSolutionQuestionId,
      stemId,
    );
  }

  /** Gets the form data without optional properties. */
  getFormData(): QuestionItemV2[] {
    return getFormData(this.form.getRawValue());
  }

  getContentJsonPathFromCoordinates(
    coordinates: ActiveQuestionEditorSubItemCoordinates & ActiveQuestionEditorItemCoordinates,
  ): string {
    return buildContentJsonPath(coordinates);
  }

  setFormState(
    questionItems: QuestionItemV2[] | null,
    options: QuestionEditingOptions | null,
    _source?: QuestionItemsSource,
  ) {
    if (!options || !questionItems) {
      return;
    }

    // check if the format of the items is still the same.
    const currentItems = this.form.getRawValue();

    // A try-catch to make sure that setting the value won't break the editor.
    // If the action fails, fallback to the default behavior (resetting the form).
    try {
      const structuralChange = isStructuralChange(currentItems, questionItems);

      if (!structuralChange) {
        // if the structure is the same, we can just set the value of the form.
        // This prevents resetting the active item (the attached form controls are still valid)
        this.form.setValue(questionItems as ReturnType<typeof this.form.getRawValue>);
        return;
      }
    } catch (error) {
      console.debug(
        'resetFormToState: structural change failed, falling back to default behavior (resetting the form)',
        error,
      );
    }

    this.setActiveQuestionItem(null);
    populateQuestionItemsFormArray(this.form, questionItems, options);
    this.formStructureChanged$.next();
  }

  private findStemIdByCoordinates(
    questionItems: QuestionItemV2[],
    coordinates: { groupIndex: number | null; stemIndex: number },
  ): number | null {
    const coordMap = new Map();
    let idCounter = 0;

    questionItems.forEach((item, rootIdx) => {
      if (item.type === QuestionItemTypeEnum.Stem) {
        coordMap.set(`null,${rootIdx}`, idCounter++);
      } else if (item.type === QuestionItemTypeEnum.StemGroup) {
        item.stems.forEach((_, insideIdx) => {
          coordMap.set(`${rootIdx},${insideIdx}`, idCounter++);
        });
      }
    });

    const key = `${coordinates.groupIndex},${coordinates.stemIndex}`;
    if (!coordMap.has(key)) {
      throw new Error(`No stem found at coordinate [${key}]`);
    }
    return coordMap.get(key);
  }

  private updateFormOnSourceEmission() {
    // A `0` timeout is needed to ensure that we don't access the abstract `questionItems$` too soon.
    return timer(0).pipe(
      switchMap(() =>
        combineLatest({
          data: merge(
            // Additionally reset the history when the source changes
            defer(() =>
              this.questionItems$.pipe(
                tap(() => this.historyManager.clearHistory()),
                map((items) => ({
                  source: QuestionItemsSource.QuestionItems,
                  items,
                })),
              ),
            ),
            this.jsonEditorUpdatedQuestionItems$.pipe(
              map((items) => ({
                source: QuestionItemsSource.JsonEditor,
                items,
              })),
            ),
            this.historyManager.navigationEvent$.pipe(
              map(({ state }) => ({
                source: QuestionItemsSource.HistoryManager,
                items: state,
              })),
            ),
          ),
          options: this.editingOptions$,
        }),
      ),
      tap(({ data, options }) => this.setFormState(data.items, options, data.source)),
    );
  }

  private async processQuestionItems(data: QuestionItemV2[]): Promise<QuestionItemV2[]> {
    return Promise.all(
      data.map(async (item) => {
        if (item.type === QuestionItemTypeEnum.Stem) {
          return await this.processStem(item);
        }

        if (item.type === QuestionItemTypeEnum.Stimulus) {
          return {
            ...item,
            content_items: await this.processContentItems(item.content_items),
          };
        }

        if (item.type === QuestionItemTypeEnum.StemGroup) {
          return {
            ...item,
            content_items: await this.processContentItems(item.content_items),
            stems: await Promise.all(item.stems.map((stem) => this.processStem(stem))),
          };
        }

        if (item.type === QuestionItemTypeEnum.Options) {
          return {
            ...item,
            content_items: await this.processContentItems(item.content_items),
            options: await Promise.all(
              item.options.map(async (option) => ({
                ...option,
                content_items: await this.processContentItems(option.content_items),
              })),
            ),
          };
        }

        return item;
      }),
    );
  }

  private async processStem(stem: StemItemV2): Promise<StemItemV2> {
    return {
      ...stem,
      content_items: await this.processContentItems(stem.content_items),
      markscheme: await this.processMarkscheme(stem.markscheme),
    };
  }

  private async processMarkscheme(markscheme: StemMarkScheme | null): Promise<StemMarkScheme | null> {
    if (!markscheme) {
      return null;
    }

    return {
      ...markscheme,
      all_mark_groups: await Promise.all(
        markscheme.all_mark_groups.map(async (markGroup) => ({
          ...markGroup,
          criterion: await this.processContentItems(markGroup.criterion),
        })),
      ),
      solution_paths: await Promise.all(
        markscheme.solution_paths.map(async (solutionPath) => ({
          ...solutionPath,
          worked_solution: await this.processContentItems(solutionPath.worked_solution),
        })),
      ),
    };
  }

  private async processContentItems(data: ContentItem[]): Promise<ContentItem[]> {
    return Promise.all(
      data.map(async (item) => {
        if (item.type === ContentItemTypeEnum.Image) {
          const processedImage = await this.questionImageCacheService.processImage(item);

          return {
            ...item,
            ...processedImage,
          };
        }

        return item;
      }),
    );
  }

  private updateHistoryOnFormChanges() {
    // A `0` timeout is needed to ensure that we don't access the abstract `this.form` too soon.
    return timer(0).pipe(
      switchMap(() => merge(this.form.valueChanges, this.jsonEditorUpdatedQuestionItems$)),
      // Merge all form changes into a single event.
      debounceTime(0),
      // Debounce is added in the history manager to not save too many states.
      tap(() => this.historyManager.pushState(this.form.getRawValue(), { ignoreEqual: true })),
    );
  }
}
